/**
 * Combobox — Single-select with autocomplete search
 * Built on shadcn Popover + Command (cmdk)
 *
 * For very large option lists (hundreds+), pass `maxRenderedOptions` to
 * switch into autocomplete mode: cmdk's built-in indexing is turned off and
 * we substring-filter in memory, rendering only the top N matches (with an
 * overflow hint). This keeps the dropdown instant to open instead of
 * mounting and indexing the entire list on every open. Omitting the prop
 * preserves the original behavior exactly (cmdk filters and renders every
 * option), so existing consumers are unaffected.
 */

import React, { useState } from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface ComboboxOption {
  value: string
  label: string
}

export interface ComboboxProps {
  options: ComboboxOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  disabled?: boolean
  /**
   * Opt-in autocomplete mode for large lists. When set, cmdk's built-in
   * filtering is disabled and only the top `maxRenderedOptions` substring
   * matches are rendered, with an overflow hint. Omit for the default
   * behavior (cmdk filters and renders every option).
   */
  maxRenderedOptions?: number
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  className,
  disabled = false,
  maxRenderedOptions,
}: ComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  const largeList = typeof maxRenderedOptions === "number"

  const selectedLabel = options.find((o) => o.value === value)?.label

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setSearch("")
  }

  const selectOption = (optionValue: string) => {
    onChange(optionValue === value ? "" : optionValue)
    setOpen(false)
    setSearch("")
  }

  const renderItem = (option: ComboboxOption) => (
    <CommandItem
      key={option.value}
      value={option.label}
      onSelect={() => selectOption(option.value)}
    >
      {option.label}
      <Check
        size={14}
        className={cn(
          "ml-auto",
          value === option.value ? "opacity-100" : "opacity-0"
        )}
      />
    </CommandItem>
  )

  // Autocomplete mode: substring-filter in memory and cap the rendered rows
  // so the dropdown never mounts the whole list at once.
  const query = search.trim().toLowerCase()
  const filtered =
    largeList && query
      ? options.filter((o) => o.label.toLowerCase().includes(query))
      : options
  const visible = largeList ? filtered.slice(0, maxRenderedOptions) : filtered
  const overflow = largeList ? filtered.length - visible.length : 0

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "justify-between font-normal border-brand-steel/50 bg-white hover:bg-white text-sm",
            !value && "text-brand-steel",
            className
          )}
        >
          <span className="truncate">{selectedLabel ?? placeholder}</span>
          <ChevronsUpDown size={14} className="ml-2 shrink-0 text-brand-steel" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={!largeList}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={largeList ? search : undefined}
            onValueChange={largeList ? setSearch : undefined}
          />
          <CommandList>
            {largeList ? (
              visible.length === 0 ? (
                <div className="py-6 text-center text-sm text-brand-steel">
                  {emptyMessage}
                </div>
              ) : (
                <>
                  <CommandGroup>{visible.map(renderItem)}</CommandGroup>
                  {overflow > 0 && (
                    <div className="border-t border-brand-steel/20 px-3 py-2 text-xs text-brand-steel/70">
                      {overflow} more — keep typing to narrow
                    </div>
                  )}
                </>
              )
            ) : (
              <>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>{options.map(renderItem)}</CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default Combobox
